import { useSyncExternalStore } from 'react';

/* ------------------------------------------------------------------ *
 * Client Admin — Roles & permissions: shared model + a tiny in-memory
 * store so the separate list and add pages stay in sync. Mock only
 * (nothing is persisted; a reload resets to the seed roles).
 * ------------------------------------------------------------------ */

export const LEVELS = ['Division', 'District', 'Block', 'Gram Panchayat'];
export const PLURAL: Record<string, string> = {
  Division: 'divisions',
  District: 'districts',
  Block: 'blocks',
  'Gram Panchayat': 'gram panchayats',
};
export const EX: Record<string, string> = {
  Division: 'Jaipur division',
  District: 'Alwar district',
  Block: 'Kathumar block',
  'Gram Panchayat': 'Khoh gram panchayat',
};
export const OTHER: Record<string, string> = {
  Division: 'Bikaner division',
  District: 'Jaipur district',
  Block: 'Rajgarh block',
  'Gram Panchayat': 'a neighbouring gram panchayat',
};
export const ALL = ['view', 'create', 'edit', 'assign', 'approve', 'close'];
export const ACT_LABEL: Record<string, string> = {
  view: 'View',
  create: 'Create',
  edit: 'Edit',
  assign: 'Assign',
  approve: 'Approve',
  close: 'Close / Delete',
};

export interface ModuleDef {
  id: string;
  group: string;
  name: string;
  desc: string;
  acts: string[];
  vis: boolean;
}
export const MODULES: ModuleDef[] = [
  { id: 'cr', group: 'Operations', name: 'Citizen Requests', desc: 'Raised by citizens; routed by program and territory', acts: ALL, vis: true },
  { id: 'dd', group: 'Operations', name: 'Daily Directions', desc: 'Instructions issued from HQ to the field', acts: ALL, vis: true },
  { id: 'en', group: 'Operations', name: 'Engagements', desc: 'Events, visits and outreach tasks', acts: ALL, vis: true },
  { id: 'rp', group: 'Operations', name: 'Reports', desc: 'Dashboards and exports', acts: ['view', 'create'], vis: true },
  { id: 'us', group: 'Administration', name: 'Users', desc: 'Invite users and assign roles', acts: ['view', 'create', 'edit', 'assign', 'close'], vis: false },
  { id: 'ro', group: 'Administration', name: 'Roles & permissions', desc: 'Define roles and their scope', acts: ['view', 'create', 'edit', 'close'], vis: false },
  { id: 'tp', group: 'Administration', name: 'Territories & programs', desc: 'Master data that roles are scoped by', acts: ['view', 'create', 'edit', 'close'], vis: false },
];

export type Terr = 'org' | 'assigned';
export type PermMap = Record<string, Record<string, boolean>>;
export type VisMap = Record<string, string>;

export interface Role {
  id: string;
  name: string;
  terr: Terr;
  level: string;
  users: number;
  perms: PermMap;
  vis: VisMap;
}

export const typeOf = (terr: Terr) => (terr === 'org' ? 'Functional' : 'Territorial');
export const scopeLine = (r: { terr: Terr; level: string }) => (r.terr === 'org' ? 'Org-wide' : `Per ${r.level}`);
export const typeChip = (type: string) =>
  type === 'Functional' ? 'bg-brand-shade text-brand' : 'bg-[#fef3e2] text-[#b45309]';
export const permCount = (perms: PermMap) =>
  MODULES.reduce((n, m) => n + m.acts.filter((a) => perms[m.id]?.[a]).length, 0);
export const PERM_TOTAL = MODULES.reduce((n, m) => n + m.acts.length, 0);

export const emptyPerms = (): PermMap => {
  const p: PermMap = {};
  MODULES.forEach((m) => {
    p[m.id] = {};
    m.acts.forEach((a) => {
      p[m.id][a] = false;
    });
  });
  return p;
};
export const emptyVis = (): VisMap => {
  const v: VisMap = {};
  MODULES.forEach((m) => {
    v[m.id] = 'scope';
  });
  return v;
};
function permsFromDef(def: Record<string, string>): PermMap {
  const p = emptyPerms();
  MODULES.forEach((m) => {
    const on = (def[m.id] || '').split(' ').filter(Boolean);
    m.acts.forEach((a) => {
      p[m.id][a] = on.indexOf(a) > -1;
    });
  });
  return p;
}

const SEED: { id: string; name: string; terr: Terr; level: string; users: number; def: Record<string, string> }[] = [
  { id: 'lpm', name: 'Library Program Manager', terr: 'assigned', level: 'District', users: 6, def: { cr: 'view edit assign close', dd: 'view', en: 'view create edit assign close', rp: 'view' } },
  { id: 'slpm', name: 'State Library Program Manager', terr: 'org', level: 'District', users: 1, def: { cr: 'view assign approve', dd: 'view create edit assign', en: 'view approve', rp: 'view create' } },
  { id: 'dhpo', name: 'District Health Program Officer', terr: 'assigned', level: 'District', users: 12, def: { cr: 'view edit assign close', dd: 'view', en: 'view create edit assign close', rp: 'view' } },
  { id: 'dc', name: 'District Coordinator', terr: 'assigned', level: 'District', users: 33, def: { cr: 'view create edit assign approve close', dd: 'view create edit assign', en: 'view create edit assign approve close', rp: 'view create' } },
  { id: 'bc', name: 'Block Coordinator', terr: 'assigned', level: 'Block', users: 48, def: { cr: 'view create edit assign', dd: 'view', en: 'view create edit', rp: 'view' } },
  { id: 'smi', name: 'State Media In-charge', terr: 'org', level: 'District', users: 1, def: { dd: 'view create', en: 'view create edit', rp: 'view' } },
  { id: 'am', name: 'Accounts Manager', terr: 'org', level: 'District', users: 2, def: { en: 'view', rp: 'view create' } },
  { id: 'ita', name: 'IT Administrator', terr: 'org', level: 'District', users: 1, def: { rp: 'view', us: 'view create edit assign close', ro: 'view create edit close', tp: 'view create edit close' } },
];

let roles: Role[] = SEED.map((s) => ({
  id: s.id,
  name: s.name,
  terr: s.terr,
  level: s.level,
  users: s.users,
  perms: permsFromDef(s.def),
  vis: emptyVis(),
}));
let seq = 1;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function getRoles(): Role[] {
  return roles;
}
export function getRole(id: string): Role | undefined {
  return roles.find((r) => r.id === id);
}
export function addRole(r: Omit<Role, 'id' | 'users'>): Role {
  const created: Role = { ...r, id: `r${seq++}`, users: 0 };
  roles = [created, ...roles];
  emit();
  return created;
}
export function deleteRole(id: string): void {
  roles = roles.filter((r) => r.id !== id);
  emit();
}

export function useClientRoles(): Role[] {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    getRoles,
    getRoles,
  );
}
